import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToggle,
} from '@commenda/kakashi';
import { WorkflowStepDescription } from '../../../components';
import { TaskStatus } from '@prisma/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { JsonSchemaForm } from '../../../json-schema-form/JsonSchemaForm';
import { TCommonTaskProp } from '../../types';
import { updateTaskStatus } from '../../utils';

type TReviewItem = {
  isValid: boolean;
  comment: string;
};

export const HumanReviewModal = ({
  isOpen,
  onClose,
  title,
  description,
  task,
  context,
  outputSchema,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  task: TCommonTaskProp['task'];
  context: TCommonTaskProp['context'];
  outputSchema?: Record<string, any>;
}) => {
  const { setNotification } = useNotification();
  const isSubmitting = useToggle(false);

  const stepExecution = useMemo(() => {
    if (!context.workflowExecution?.runtime?.stepExecutions) return undefined;
    return Object.values(
      context.workflowExecution.runtime.stepExecutions
    ).find(
      (execution) =>
        execution.stepDefinitionId === task.workflowDefinitionStepId
    );
  }, [context.workflowExecution, task.workflowDefinitionStepId]);

  const inputData = stepExecution?.inputData?.data;
  const isArray = Array.isArray(inputData);

  const dataColumns = useMemo(() => {
    if (!isArray || inputData.length === 0) return [];
    const firstItem = inputData[0];
    if (typeof firstItem !== 'object' || firstItem === null) return [];
    return Object.keys(firstItem).filter((key) => !['sourceRecordId', 'isValid', 'comment'].includes(key));
  }, [inputData, isArray]);

  const existingReviews = stepExecution?.outputData?.reviews as
    | TReviewItem[]
    | undefined;

  const [reviews, setReviews] = useState<TReviewItem[]>([]);

  useEffect(() => {
    if (!isArray) return;
    if (existingReviews && existingReviews.length === inputData.length) {
      setReviews(existingReviews);
    } else {
      setReviews(
        inputData.map((_: unknown, i: number) => ({
          isValid: existingReviews?.[i]?.isValid ?? true,
          comment: existingReviews?.[i]?.comment ?? '',
        }))
      );
    }
  }, [inputData, isArray, existingReviews]);

  const updateReview = useCallback(
    (index: number, patch: Partial<TReviewItem>) => {
      setReviews((prev) =>
        prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
      );
    },
    []
  );

  const mergedReviews = useMemo(() => {
    if (!isArray) return [];
    return inputData.map((item: Record<string, unknown>, i: number) => ({
      ...item,
      isValid: reviews[i]?.isValid ?? true,
      comment: reviews[i]?.comment ?? '',
    }));
  }, [inputData, isArray, reviews]);

  const hasValidationErrors = useMemo(() => {
    return reviews.some((r) => !r.isValid && !r.comment.trim());
  }, [reviews]);

  const handleSave = async () => {
    isSubmitting.on();
    const result = await updateTaskStatus({
      task,
      mutate: context.mutate,
      status: TaskStatus.IN_PROGRESS,
      metadata: { outputData: { reviewedData: mergedReviews } },
    });
    if (!result) {
      setNotification({
        title: 'Error',
        message: 'Failed to save review',
        valid: false,
        show: true,
      });
      isSubmitting.off();
      return;
    }
    setNotification({
      title: 'Success',
      message: 'Review saved',
      valid: true,
      show: true,
    });
    isSubmitting.off();
    onClose();
  };

  const handleComplete = async () => {
    if (hasValidationErrors) {
      setNotification({
        title: 'Validation Error',
        message: 'Comment is required for items marked as invalid',
        valid: false,
        show: true,
      });
      return;
    }
    isSubmitting.on();
    const result = await updateTaskStatus({
      task,
      mutate: context.mutate,
      status: TaskStatus.COMPLETED,
      metadata: { outputData: { reviewedData: mergedReviews } },
    });
    if (!result) {
      setNotification({
        title: 'Error',
        message: 'Failed to complete review',
        valid: false,
        show: true,
      });
      isSubmitting.off();
      return;
    }
    setNotification({
      title: 'Success',
      message: 'Review completed',
      valid: true,
      show: true,
    });
    isSubmitting.off();
    onClose();
  };

  const handleMarkAsComplete = async (outputData?: Record<string, any>) => {
    isSubmitting.on();
    const result = await updateTaskStatus({
      task,
      mutate: context.mutate,
      status: TaskStatus.COMPLETED,
      metadata: outputData ? { outputData } : undefined,
    });
    if (!result) {
      setNotification({
        title: 'Error',
        message: 'Failed to complete step',
        valid: false,
        show: true,
      });
      isSubmitting.off();
      return;
    }
    setNotification({
      title: 'Success',
      message: 'Step completed',
      valid: true,
      show: true,
    });
    isSubmitting.off();
    onClose();
  };

  const renderCellValue = (value: unknown) => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const isCompleted = task.status === TaskStatus.COMPLETED;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <WorkflowStepDescription description={description} />
        </DialogHeader>

        <div className="flex-1 overflow-auto p-6">
          {!isArray ? (
            <div className="flex flex-col gap-4">
              <p className="text-text-secondary">Review Not Supported.</p>
              {outputSchema ? (
                <JsonSchemaForm
                  schema={outputSchema}
                  onSubmit={(data) => handleMarkAsComplete(data)}
                  defaultData={stepExecution?.outputData}
                  readonly={isCompleted}
                />
              ) : (
                <Button
                  onClick={() => handleMarkAsComplete()}
                  disabled={isSubmitting.value || isCompleted}
                >
                  Mark as Complete
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">#</TableHead>
                  {dataColumns.map((col) => (
                    <TableHead key={col}>{col}</TableHead>
                  ))}
                  <TableHead className="w-24">Valid</TableHead>
                  <TableHead className="min-w-[200px]">Comment</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inputData.map(
                  (item: Record<string, unknown>, index: number) => {
                    const review = reviews[index];
                    if (!review) return null;
                    return (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        {dataColumns.map((col) => (
                          <TableCell key={col}>
                            {renderCellValue(item[col])}
                          </TableCell>
                        ))}
                        <TableCell>
                          <Checkbox
                            checked={review.isValid}
                            onCheckedChange={(checked: boolean) =>
                              updateReview(index, { isValid: checked })
                            }
                            disabled={isCompleted}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={review.comment}
                            onChange={(e) =>
                              updateReview(index, {
                                comment: e.target.value,
                              })
                            }
                            placeholder={
                              !review.isValid ? 'Required' : 'Optional'
                            }
                            disabled={isCompleted}
                            className={
                              !review.isValid && !review.comment.trim()
                                ? 'border-border-error'
                                : ''
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {isArray && !isCompleted && (
          <DialogFooter className="p-6">
            <Button
              variant="secondary"
              onClick={handleSave}
              disabled={isSubmitting.value}
            >
              Save
            </Button>
            <Button
              onClick={handleComplete}
              disabled={isSubmitting.value || hasValidationErrors}
            >
              Complete Review
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
